import React, { useMemo, useState } from 'react';
import { add } from 'date-fns';
import { DatesRangeValue } from '@hud/web-components/DatePicker/DatePicker';
import {
  ExplicitTimeFrame,
  RelativeTimeFrame,
  TimeFrame,
} from '@hud/portal-dtos/webapp-common.dto';
import useTimeFrame from '../../hooks/useTimeFrame';
import Styled from './TimeFrameMenu.styles';
import StyledPopover from '@hud/web-components/Popover/Popover.styles';
import { Button } from '@hud/web-components/Button/Button';
import { useClickOutside } from '@mantine/hooks';
import {
  formatDateShort,
  formatTime12Hour,
  getEndOfDay,
} from '@hud/web-components/utils';
import { TimeSelect } from './TimeSelect';
import { useBooleanFeatureFlag } from '../../hooks/useFeatureFlags';

const BASE_TIMEFRAME_TO_DISPLAY: {
  key: RelativeTimeFrame;
  value: string;
}[] = [
  { key: 'hour', value: 'Last hour' },
  { key: 'day', value: 'Last 24h' },
  { key: 'week', value: 'Last 7 days' },
  { key: '14days', value: 'Last 14 days' },
  { key: '30days', value: 'Last 30 days' },
  { key: '2months', value: 'Last 2 months' },
];

export default function BaseTimeFrameMenu() {
  const enable1HourTimeFrame = useBooleanFeatureFlag('enable-1-hour-timeframe');
  const enableTimeTimePicker = useBooleanFeatureFlag('enable-time-timepicker');
  const [opened, setOpened] = useState(false);
  const [timeSelectOpen, setTimeSelectOpen] = useState(false);
  const [selectedTimeFrame, setSelectedTimeFrame] = useState<TimeFrame | null>(null);
  const [selectedDateRange, setSelectedDateRange] = useState<DatesRangeValue>([
    null,
    null,
  ]);
  // If only a start date is selected, we assume the user wanted to pick a singular date and derive the to date&time accordingly
  const derivedToDate =
    selectedDateRange[1] ?? getEndOfDay(selectedDateRange[0] ?? new Date());
  const [showDatePicker, setShowDatePicker] = useState(false);
  const { timeFrame, setTimeFrame } = useTimeFrame();
  const ref = useClickOutside(() => {
    // don't close the menu if the time select dropdown is open
    if (!timeSelectOpen) {
      setOpened(false);
    }
  }, ['mouseup', 'touchend']);

  function onDatePicked(value: DatesRangeValue) {
    // set the end date to the end of the day, rather than its start
    const today = new Date();
    if (value[1]) {
      // If value[1] is today, set it to the current time + hour
      // else set it to 23:59
      if (
        value[1].getDate() === today.getDate() &&
        value[1].getMonth() === today.getMonth() &&
        value[1].getFullYear() === today.getFullYear()
      ) {
        value[1] = new Date();
        value[1].setHours(today.getHours() + 1);
        value[1].setMinutes(0);
        value[1].setSeconds(0);
        value[1].setMilliseconds(0);
      } else {
        value[1] = getEndOfDay(value[1]);
      }
    } else {
      // If value[1] is null and value[0] is today, set value[1] to the current time + hour
      if (
        value[0] &&
        value[0].getDate() === today.getDate() &&
        value[0].getMonth() === today.getMonth() &&
        value[0].getFullYear() === today.getFullYear()
      ) {
        value[1] = new Date();
        value[1].setHours(today.getHours() + 1);
        value[1].setMinutes(0);
        value[1].setSeconds(0);
        value[1].setMilliseconds(0);
      }
    }

    setSelectedDateRange(value);
  }

  function handleCustomRangeClicked() {
    if (!showDatePicker) {
      setSelectedTimeFrame(null);
    }
    setShowDatePicker(true);
  }

  function handleTimeFrameClicked(timeFrame: TimeFrame) {
    setSelectedTimeFrame(timeFrame);
    setTimeFrame(timeFrame);
    setShowDatePicker(false);
    setSelectedDateRange([null, null]);
    close();
  }

  function open() {
    if (typeof timeFrame === 'string') {
      setSelectedTimeFrame(timeFrame as TimeFrame);
      setShowDatePicker(false);
    } else if (timeFrame) {
      const explicitTF = timeFrame as ExplicitTimeFrame;
      setSelectedDateRange([explicitTF.start, explicitTF.end]);
      setShowDatePicker(true);
    }
    setOpened(true);
  }

  function close() {
    setOpened(false);
  }

  function handleSubmit() {
    if (selectedTimeFrame) {
      setTimeFrame(selectedTimeFrame);
    } else if (selectedDateRange && selectedDateRange[0] instanceof Date) {
      const start = selectedDateRange[0];
      const end = new Date(
        selectedDateRange[1] ||
          add(start, {
            hours: 23,
            minutes: 59,
          }),
      );
      setTimeFrame({ start, end });
    }
    close();
  }

  const timeFrameDisplay = enable1HourTimeFrame
    ? BASE_TIMEFRAME_TO_DISPLAY
    : BASE_TIMEFRAME_TO_DISPLAY.filter(({ key }) => key !== 'hour');

  function displayTimeFrame(timeFrame: RelativeTimeFrame): string {
    return (
      timeFrameDisplay.find((tf) => tf.key === timeFrame)?.value || 'Unknown Time Frame'
    );
  }

  const DateTimeLabel = ({ date, showTime }: { date: Date; showTime: boolean }) => {
    return (
      <div>
        <span>{formatDateShort(date)}</span>
        {showTime && (
          <>
            <Styled.GrayDot />
            <span>{formatTime12Hour(date)}</span>
          </>
        )}
      </div>
    );
  };

  const selectedLabel = useMemo(() => {
    if (typeof timeFrame === 'string') {
      return displayTimeFrame(timeFrame);
    }
    if (timeFrame) {
      const explicitTF = timeFrame as ExplicitTimeFrame;
      const { start, end } = explicitTF;
      const isDefaultHours =
        start.getHours() === 0 &&
        start.getMinutes() === 0 &&
        end.getHours() === 23 &&
        end.getMinutes() === 59; // show hours if not 00:00-23:59
      return (
        <>
          <DateTimeLabel date={start} showTime={!isDefaultHours} />-
          <DateTimeLabel date={end} showTime={!isDefaultHours} />
        </>
      );
    }
  }, [timeFrame, displayTimeFrame]);

  function onTimeSelectOpen() {
    setTimeSelectOpen(true);
  }

  function onTimeSelectClose() {
    setTimeSelectOpen(false);
  }

  const showTimePicker =
    typeof selectedTimeFrame !== 'string' &&
    enableTimeTimePicker &&
    selectedDateRange !== null &&
    selectedDateRange[0] !== null;
  const showFooter = typeof selectedTimeFrame !== 'string';
  const invalidTimeFrame =
    selectedDateRange[0] instanceof Date &&
    selectedDateRange[1] instanceof Date &&
    selectedDateRange[1] <= selectedDateRange[0];
  const saveTimeFrameEnabled =
    selectedDateRange && selectedDateRange[0] instanceof Date && !invalidTimeFrame;

  return (
    <StyledPopover.Popover
      unstyled
      position={'bottom-start'}
      opened={opened}
      onClose={close}
    >
      <StyledPopover.Target>
        <StyledPopover.TargetContainer $clickable>
          <Styled.Button
            onClick={() => {
              if (opened) {
                close();
              } else {
                open();
              }
            }}
          >
            <Styled.Icon icon="other.timer" size="14px" />
            {selectedLabel}
            <Styled.CarretIcon />
          </Styled.Button>
        </StyledPopover.TargetContainer>
      </StyledPopover.Target>
      <StyledPopover.Dropdown ref={ref}>
        <Styled.Body>
          <Styled.Content>
            <Styled.Menu>
              {timeFrameDisplay.map(({ key, value }) => (
                <Styled.MenuOption
                  key={key}
                  $selected={selectedTimeFrame === key}
                  onClick={() => handleTimeFrameClicked(key as TimeFrame)}
                >
                  {value}
                </Styled.MenuOption>
              ))}
              <Styled.MenuOption
                $selected={showDatePicker}
                onClick={handleCustomRangeClicked}
              >
                Custom Range
              </Styled.MenuOption>
            </Styled.Menu>
            {showDatePicker ? (
              <Styled.DatePicker
                defaultDate={(selectedDateRange && selectedDateRange[1]) ?? new Date()}
                maxDate={new Date()}
                value={selectedDateRange ?? [null, null]}
                onChange={onDatePicked}
              />
            ) : null}
          </Styled.Content>
          {showTimePicker && (
            <>
              <Styled.Divider />
              <Styled.TimePicker className={invalidTimeFrame ? 'error' : ''}>
                <span>From</span>
                <TimeSelect
                  value={selectedDateRange[0]}
                  onChange={(newValue) => {
                    setSelectedDateRange([newValue, derivedToDate]);
                  }}
                  onDropdownOpen={onTimeSelectOpen}
                  onDropdownClose={onTimeSelectClose}
                />
                <Styled.TimePickerSpacer>-</Styled.TimePickerSpacer>
                <span>To</span>
                <TimeSelect
                  value={derivedToDate}
                  onChange={(newValue) => {
                    // if a single day is selected (end date not set), set it to the same day as the start date with hours:minutes set to 23:59
                    let toDate = newValue;
                    if (!selectedDateRange[1]) {
                      toDate = getEndOfDay(selectedDateRange[0] ?? new Date());
                      toDate.setHours(
                        newValue?.getHours() ?? 23,
                        newValue?.getMinutes() ?? 59,
                      );
                    }
                    setSelectedDateRange([selectedDateRange[0], toDate]);
                  }}
                  onDropdownOpen={onTimeSelectOpen}
                  onDropdownClose={onTimeSelectClose}
                  error={invalidTimeFrame}
                />
              </Styled.TimePicker>
            </>
          )}
          {showFooter && (
            <>
              <Styled.Divider />
              <Styled.Footer>
                {invalidTimeFrame && (
                  <div className="validationError">Select valid time</div>
                )}
                <Button onClick={close}>Cancel</Button>
                <Button
                  variant="glow"
                  onClick={handleSubmit}
                  disabled={!saveTimeFrameEnabled}
                >
                  Save
                </Button>
              </Styled.Footer>
            </>
          )}
        </Styled.Body>
      </StyledPopover.Dropdown>
    </StyledPopover.Popover>
  );
}
